import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  alpha,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  Grid2,
  MenuItem,
  OutlinedInput,
  Select,
  Slide,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import CloseIcon from "assets/svg/CloseIcon";
import PencilIcon from "assets/svg/PencilIcon";
import { AxiosError, AxiosResponse } from "axios";
import AdminPageCardTitle from "components/AdminPageCardTitle/AdminPageCardTitle";
import PrimaryButton from "components/PrimaryButton/PrimaryButton";
import { Field, Form, Formik } from "formik";
import { DateTime } from "luxon";
import { commonStyle } from "pages/Dashboard/common.styles";
import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  AdminUserDetailPersonalDetails,
  AdminUserDetailResponse,
  AdminWalletData,
  AdminWalletResponse,
  TransactionsListData,
  TransactionsResponse,
} from "reduxStore/dataTypes";
import { RootState } from "reduxStore/store";
import {
  fetchAdminUserDetails,
  updateAdminProfile,
  userStatusUpdate,
  adminWalletDetails,
  fetchAdminTransactions,
} from "services/apiService";
import theme, { responsiveFontSize } from "themes/Theme";
import { getStatusBgColor } from "utils/common";
import { usersProfileUpdateSchema } from "validations/ValidationsSchema";
import { useDropzone } from "react-dropzone";
import imageCompression from "browser-image-compression";
import Cropper from "react-easy-crop";
import { DataGrid } from "@mui/x-data-grid";
import { transactionColumn } from "../TransactionPage/dataGridDef";

const styles = {
  container: {
    flexGrow: 1,
    borderRadius: 3,
    boxShadow: `rgba(8, 60, 61, 0.27) 0px 1px 5px 0px, rgba(8, 60, 61, 0.06) 0px 1px 5px 0px`,
    backgroundColor: "rgb(255, 255, 255)",
    p: 3,
    ml: { xs: 0, md: -5 },
  },
  inputLabelStyle: {
    fontSize: responsiveFontSize(14),
    fontWeight: 400,
    color: theme.palette.text.primary,
    width: "15em",
  },
  inputValueStyle: {
    fontSize: responsiveFontSize(14),
    fontWeight: 400,
    color: theme.palette.primary.main,
  },
};

const UserDetailsPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const { user } = useSelector((state: RootState) => state.adminAuth);
  const [image, setImage] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedImage, setCroppedImage] = useState<string | null>(null);
  const [userDetails, setUserDetails] =
    useState<AdminUserDetailPersonalDetails>();
  const [walletData, setWalletData] = useState<AdminWalletData | null>(null);
  const [transactionData, setTransactionData] = useState<
    TransactionsListData[]
  >([]);
  const [walletLoading, setWalletLoading] = useState(false);
  const [transactionsLoading, setTransactionsLoading] = useState(false);

  const getUserDetails = async () => {
    try {
      if (!id) return;
      const response: AxiosResponse<AdminUserDetailResponse> =
        await fetchAdminUserDetails(id);
      if (response && response.status === 200) {
        setUserDetails(response?.data?.data?.personal_details);
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        toast.error(error?.response?.data?.message);
      }
    }
  };

  const getWalletAndTransactions = async () => {
    if (!id) return;
    setWalletLoading(true);
    setTransactionsLoading(true);
    try {
      // Fetch wallet
      const walletRes = await adminWalletDetails({ user_id: id });
      if (
        walletRes &&
        walletRes.status === 200 &&
        walletRes.data?.data?.length > 0
      ) {
        setWalletData(walletRes.data.data[0]);
      } else {
        setWalletData(null);
      }
    } catch (e) {
      setWalletData(null);
    } finally {
      setWalletLoading(false);
    }
    try {
      // Fetch transactions (limit 5)
      const txRes = await fetchAdminTransactions({
        user_id: id,
        limit: 5,
        page: 1,
      });
      if (txRes && txRes.status === 200 && txRes.data?.data?.transactions) {
        setTransactionData(txRes.data.data.transactions);
      } else {
        setTransactionData([]);
      }
    } catch (e) {
      setTransactionData([]);
    } finally {
      setTransactionsLoading(false);
    }
  };

  useEffect(() => {
    getUserDetails();
    getWalletAndTransactions();
    // eslint-disable-next-line
  }, []);

  const handleUserStatusUpdate = async (status: string) => {
    try {
      const response = await userStatusUpdate(id, status);
      if (response && response.status === 200) {
        toast.success(response?.data?.message);
        getUserDetails();
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        toast.error(error?.response?.data?.message);
      }
    }
  };

  const validate = (values: {
    firstName: string;
    lastName: string;
    phoneNumber: string;
    dob: string;
    profilePic: string;
    status: string;
    role: string;
    password?: string;
  }) => {
    const result = usersProfileUpdateSchema.safeParse(values);
    let errors = result.success ? {} : result.error.format();

    // Custom password validation
    if (
      values.password &&
      values.password.length > 0 &&
      values.password.length < 8
    ) {
      errors = {
        ...errors,
        password: { _errors: ["Password must be at least 8 characters long."] },
      };
    }
    return errors;
  };

  const RenderField = ({ label, value }: any) => {
    return (
      <Stack direction={"row"} alignItems={"center"} gap={1} mt={1}>
        <Typography sx={styles.inputLabelStyle}>{label}</Typography>
        <Typography sx={styles.inputValueStyle}>{value}</Typography>
      </Stack>
    );
  };

  const handleUpdateProfile = async (
    values: {
      firstName: string;
      lastName: string;
      phoneNumber: string;
      dob: string;
      profilePic: string;
      status: string;
      role: string;
      password?: string;
    },
    resetForm: () => void
  ) => {
    try {
      setLoading(true);
      const body = {
        firstName: values.firstName,
        lastName: values.lastName,
        phoneNumber: values.phoneNumber,
        dob: values.dob,
        profilePic: croppedImage || values.profilePic,
        status: values.status,
        role: values.role || userDetails?.role,
        ...(values.password ? { password: values.password } : {}),
      };

      const response = await updateAdminProfile(id, body);
      if (response && response.status === 200) {
        toast.success("Profile updated successfully");
        setEditMode(false);
        setCroppedImage(null);
        setImage(null);
        resetForm();
        getUserDetails();
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        toast.error(error.response?.data?.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const NonEditableFields = () => {
    return (
      <Box>
        <Box sx={commonStyle.flexSpaceBetween}>
          <Stack direction={"row"} alignItems={"center"} gap={1} my={4}>
            <Avatar
              sx={{
                width: 54,
                height: 54,
                aspectRatio: "1/1",
                objectFit: "cover",
              }}
              variant="rounded"
              src={userDetails?.profilePic}
            />
            <Box>
              <Typography
                sx={{
                  ...styles.inputValueStyle,
                  fontSize: responsiveFontSize(18),
                }}
              >{`${userDetails?.firstName} ${userDetails?.lastName}`}</Typography>
              <Typography
                sx={{
                  ...styles.inputValueStyle,
                  color: theme.palette.text.secondary,
                }}
              >
                {userDetails?.email}
              </Typography>
            </Box>
          </Stack>
        </Box>

        <RenderField
          label={"Phone Number :"}
          value={`${userDetails?.phoneNumber ? userDetails?.phoneNumber : "-"}`}
        />
        <Divider
          sx={{ borderColor: alpha(theme.palette.text.primary, 0.2), my: 1 }}
        />
        <RenderField
          label={"Date of Birth :"}
          value={`${userDetails?.dob ? userDetails?.dob : "-"}`}
        />
        <Divider
          sx={{ borderColor: alpha(theme.palette.text.primary, 0.2), my: 1 }}
        />
        <RenderField
          label={"Two Factor Authentication :"}
          value={`${
            userDetails?.twoFactorEnabled === 1 ? "Enabled" : "Disabled"
          }`}
        />
        <Divider
          sx={{ borderColor: alpha(theme.palette.text.primary, 0.2), my: 1 }}
        />
        <Stack direction={"row"} alignItems={"center"} gap={1} mt={1}>
          <Typography sx={styles.inputLabelStyle}>{"Status"}</Typography>
          <Box
            sx={{
              backgroundColor: alpha(
                getStatusBgColor(userDetails?.status?.toLowerCase()),
                0.1
              ),
              borderRadius: 2,
              px: 2,
              py: 0.5,
            }}
          >
            <Typography
              sx={{
                ...styles.inputValueStyle,
                color: getStatusBgColor(userDetails?.status?.toLowerCase()),
              }}
            >
              {userDetails?.status}
            </Typography>
          </Box>
        </Stack>
        <Divider
          sx={{ borderColor: alpha(theme.palette.text.primary, 0.2), my: 1 }}
        />
        <RenderField
          label={"Role :"}
          value={`${userDetails?.role ? userDetails?.role : "-"}`}
        />
        <Divider
          sx={{ borderColor: alpha(theme.palette.text.primary, 0.2), my: 2 }}
        />
        <Box mb={2}>
          <Typography sx={{ ...styles.inputLabelStyle, mb: 1 }}>
            Wallet Balance:
          </Typography>
          {walletLoading ? (
            <CircularProgress size={20} />
          ) : walletData ? (
            <Typography
              sx={{
                ...styles.inputValueStyle,
                fontSize: responsiveFontSize(16),
              }}
            >
              {walletData.balance} {walletData.cryptoTicker}
            </Typography>
          ) : (
            <Typography sx={styles.inputValueStyle}>-</Typography>
          )}
        </Box>
        <Box>
          <Typography sx={{ ...styles.inputLabelStyle, mb: 1 }}>
            Recent Transactions:
          </Typography>
          <DataGrid
            rows={transactionData || []}
            columns={transactionColumn.filter(
              (column) => column.field !== "name"
            )}
            getRowId={(row) => row.transactionId}
            loading={transactionsLoading}
            autoHeight
            hideFooterPagination
            disableRowSelectionOnClick
            sx={{ background: "#fafbfc", borderRadius: 2, mb: 2 }}
            slots={{
              noRowsOverlay: () => (
                <Box sx={{ p: 2, textAlign: "center" }}>
                  <Typography variant="body2">
                    No recent transactions found
                  </Typography>
                </Box>
              ),
            }}
          />
        </Box>
      </Box>
    );
  };

  const EditableFields = () => {
    return (
      <Formik
        initialValues={{
          firstName: userDetails?.firstName || "",
          lastName: userDetails?.lastName || "",
          phoneNumber: userDetails?.phoneNumber || "",
          dob: userDetails?.dob || "",
          profilePic: userDetails?.profilePic || "",
          status: userDetails?.status || "",
          role: userDetails?.role || "",
          password: "",
        }}
        validate={validate}
        onSubmit={(values, { resetForm }) => {
          handleUpdateProfile(values, resetForm);
        }}
      >
        {({ values, handleSubmit, setFieldValue }) => (
          <Form
            onSubmit={handleSubmit}
            style={{
              display: "flex",
              flexDirection: "column",
              marginTop: "1em",
              marginBottom: "1em",
            }}
          >
            <Grid2 container spacing={2} wrap="wrap">
              <Grid2 size={{ xs: 12, md: 4 }}>
                <Typography sx={commonStyle.inputLabelStyle}>
                  First Name
                </Typography>
                <Field name="firstName">
                  {({ field, meta }: any) => (
                    <TextField
                      {...field}
                      fullWidth
                      type="text"
                      size="small"
                      margin="dense"
                      slotProps={commonStyle.inputSlotTextStyle}
                      error={meta.touched && !!meta.error}
                      helperText={
                        meta.touched && meta.error
                          ? meta.error._errors?.[0]
                          : ""
                      }
                    />
                  )}
                </Field>
              </Grid2>
              <Grid2 size={{ xs: 12, md: 4 }}>
                <Typography sx={commonStyle.inputLabelStyle}>
                  Last Name
                </Typography>
                <Field name="lastName">
                  {({ field, meta }: any) => (
                    <TextField
                      {...field}
                      type="text"
                      margin="dense"
                      size="small"
                      fullWidth
                      slotProps={commonStyle.inputSlotTextStyle}
                      error={meta.touched && !!meta.error}
                      helperText={
                        meta.touched && meta.error
                          ? meta.error._errors?.[0]
                          : ""
                      }
                    />
                  )}
                </Field>
              </Grid2>
              <Grid2 size={{ xs: 12, md: 4 }}>
                <Typography sx={commonStyle.inputLabelStyle}>
                  Phone Number
                </Typography>
                <Field name="phoneNumber">
                  {({ field, meta }: any) => (
                    <TextField
                      {...field}
                      type="text"
                      margin="dense"
                      size="small"
                      fullWidth
                      slotProps={commonStyle.inputSlotTextStyle}
                      error={meta.touched && !!meta.error}
                      helperText={
                        meta.touched && meta.error
                          ? meta.error._errors?.[0]
                          : ""
                      }
                    />
                  )}
                </Field>
              </Grid2>
              <Grid2 size={{ xs: 12, md: 4 }}>
                <Typography sx={commonStyle.inputLabelStyle}>
                  Date Of Birth
                </Typography>
                <Field name="dob">
                  {({ field, form, meta }: any) => (
                    <LocalizationProvider dateAdapter={AdapterLuxon}>
                      <DatePicker
                        value={
                          field.value ? DateTime.fromISO(field.value) : null
                        }
                        maxDate={DateTime.now()}
                        disableFuture
                        onChange={(newValue: any) => {
                          form.setFieldValue(
                            field.name,
                            newValue ? newValue.toISODate() : ""
                          );
                        }}
                        format="yyyy-MM-dd"
                        sx={{
                          width: "100%",
                        }}
                        slotProps={{
                          textField: {
                            margin: "dense",
                            variant: "outlined",
                            size: "small",
                            slotProps: {
                              input: {
                                sx: {
                                  fontSize: "0.875rem",
                                  color: theme.palette.text.primary,
                                  fontWeight: 400,
                                },
                              },
                            },
                            error: meta.touched && Boolean(meta.error),
                            helperText:
                              meta.touched && meta.error
                                ? meta.error._errors?.[0]
                                : "",
                          },
                        }}
                      />
                    </LocalizationProvider>
                  )}
                </Field>
              </Grid2>
              <Grid2 size={{ xs: 12, md: 4 }}>
                <Typography sx={{ ...commonStyle.inputLabelStyle, mb: 1 }}>
                  Status
                </Typography>
                <Select
                  value={values.status}
                  onChange={(e) => {
                    setFieldValue("status", e.target.value);
                  }}
                  size="small"
                  fullWidth
                  slotProps={commonStyle.inputSlotTextStyle}
                >
                  <MenuItem value="">
                    <em>Choose Status</em>
                  </MenuItem>
                  <MenuItem value="Active">Active</MenuItem>
                  <MenuItem value="Inactive">Inactive</MenuItem>
                  <MenuItem value="Suspended">Suspended</MenuItem>
                  <MenuItem value="Unverified">Unverified</MenuItem>
                </Select>
              </Grid2>
              <Grid2 size={{ xs: 12, md: 4 }}>
                <Typography sx={commonStyle.inputLabelStyle}>
                  New Password
                </Typography>
                <Field name="password">
                  {({ field, meta }: any) => (
                    <TextField
                      {...field}
                      type="password"
                      margin="dense"
                      size="small"
                      fullWidth
                      placeholder="Leave blank to keep unchanged"
                      error={meta.touched && !!meta.error}
                      helperText={
                        meta.touched && meta.error
                          ? meta.error._errors?.[0]
                          : ""
                      }
                    />
                  )}
                </Field>
              </Grid2>
            </Grid2>
            {loading ? (
              <CircularProgress sx={{ marginTop: "1em" }} />
            ) : (
              <PrimaryButton
                title="Update Profile"
                onClick={() => handleSubmit()}
                viewStyle={{ marginTop: "1em" }}
              />
            )}
          </Form>
        )}
      </Formik>
    );
  };

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    if (acceptedFiles.length === 0) return;
    const file = acceptedFiles[0];
    const reader = new FileReader();
    reader.onload = () => setImage(reader.result as string);
    reader.readAsDataURL(file);
  }, []);

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: { "image/*": [] },
    multiple: false,
  });

  const onCropComplete = async (_: any, croppedAreaPixels: any) => {
    if (!image) return;
    try {
      const croppedBlob = await getCroppedImg(image, croppedAreaPixels);
      const croppedFile = new File([croppedBlob], "cropped-image.jpg", {
        type: "image/jpeg",
      });
      const compressedCroppedBlob = await imageCompression(croppedFile, {
        maxSizeMB: 0.3,
      });
      const base64String = await convertBlobToBase64(compressedCroppedBlob);
      setCroppedImage(base64String);
    } catch (error) {
      console.error("Cropping or compression failed:", error);
    }
  };

  return (
    <Box sx={styles.container}>
      <Box sx={commonStyle.flexSpaceBetween}>
        <AdminPageCardTitle
          title={"Users Details"}
          icon={<ArrowBackIcon sx={{ color: theme.palette.primary.main }} />}
          onIconClick={() => {
            navigate(-1);
          }}
        />
        <Box display="flex" flexDirection="row" alignItems="center" gap={2}>
          <PrimaryButton
            title={editMode ? "Cancel" : "Edit"}
            rightIcon={
              editMode ? <CloseIcon color="#FFFFFF" /> : <PencilIcon />
            }
            onClick={() => {
              setEditMode((prev) => !prev);
            }}
          />
          {!editMode &&
            (userDetails?.status?.toLowerCase() === "active" ? (
              <Button
                variant="contained"
                sx={{
                  backgroundColor: theme.palette.custom.orange,
                  borderRadius: 2,
                }}
                onClick={() => {
                  handleUserStatusUpdate("Suspended");
                }}
              >
                <Typography
                  sx={{
                    textTransform: "capitalize",
                    fontSize: responsiveFontSize(14),
                  }}
                >
                  Suspend User
                </Typography>
              </Button>
            ) : (
              <Button
                variant="contained"
                sx={{
                  backgroundColor: theme.palette.custom.green,
                  borderRadius: 2,
                }}
                onClick={() => {
                  handleUserStatusUpdate("Active");
                }}
              >
                <Typography
                  sx={{
                    textTransform: "capitalize",
                    fontSize: responsiveFontSize(14),
                  }}
                >
                  Active User
                </Typography>
              </Button>
            ))}
        </Box>
      </Box>
      {editMode ? (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent={"center"}
          sx={{ position: "relative" }}
        >
          <Box sx={{ position: "absolute", top: 0, right: 5, zIndex: 99 }}>
            <PencilIcon />
          </Box>
          <div
            {...getRootProps()}
            style={{
              position: "relative",
              border: "1px dashed gray",
              padding: 0,
              borderRadius: "50%",
              textAlign: "center",
              width: "100px",
              height: "100px",
              cursor: "pointer",
              overflow: "hidden",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <input {...getInputProps()} />

            {/* Display Cropped Image or Profile Picture */}
            {croppedImage || userDetails?.profilePic ? (
              <Avatar
                sx={{
                  width: 54,
                  height: 54,
                  aspectRatio: "1/1",
                  objectFit: "cover",
                }}
                variant="rounded"
                src={croppedImage || userDetails?.profilePic}
              />
            ) : (
              <Avatar
                sx={{
                  width: 54,
                  height: 54,
                  aspectRatio: "1/1",
                  objectFit: "cover",
                }}
                variant="rounded"
              />
            )}
          </div>

          {/* Image Cropper Dialog */}
          {image && (
            <Dialog
              open={Boolean(image)}
              onClose={() => setImage(null)}
              slots={{ transition: Slide }}
              sx={{
                "& .MuiDialog-paper": {
                  backgroundColor: "white !important", // Ensure white background
                  borderRadius: "10px",
                },
              }}
              slotProps={{
                backdrop: {
                  style: { backgroundColor: "rgba(0, 0, 0, 0.3)" },
                },
              }}
            >
              <DialogContent
                sx={{
                  width: "300px",
                  height: "300px",
                  padding: 0,
                  backgroundColor: "white",
                }}
              >
                <Cropper
                  image={image}
                  crop={crop}
                  zoom={zoom}
                  aspect={1}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={onCropComplete}
                />
              </DialogContent>
              <DialogActions>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={() => setImage(null)}
                >
                  Done
                </Button>
              </DialogActions>
            </Dialog>
          )}
        </Box>
      ) : null}
      {editMode ? <EditableFields /> : <NonEditableFields />}
    </Box>
  );
};
export default UserDetailsPage;

async function getCroppedImg(imageSrc: string, cropArea: any): Promise<Blob> {
  const img = await createImage(imageSrc);
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  if (!ctx) throw new Error("Canvas context not available");

  canvas.width = cropArea.width;
  canvas.height = cropArea.height;

  ctx.drawImage(
    img,
    cropArea.x,
    cropArea.y,
    cropArea.width,
    cropArea.height,
    0,
    0,
    cropArea.width,
    cropArea.height
  );

  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error("Failed to create cropped image blob"));
    }, "image/jpeg");
  }).catch((error) => {
    console.error("Cropping or compression failed:", error);
    throw error;
  });
}

function createImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.src = url;
    img.onload = () => resolve(img);
    img.onerror = (error) => reject(error);
  });
}

const convertBlobToBase64 = (blob: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(blob);
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = reject;
  });
};
